import { PropsWithChildren } from 'react';
import { useLoading } from '@/contexts/LoadingContext';
import { Toast } from '@/lib/Toast';
import { handleErrorAsync } from '@/utils/handlers';
import {
  Pagination as MuiPagination,
  Box,
  Typography,
  useTheme,
  useMediaQuery,
} from '@mui/material';

interface IPagination {
  totalPages: number;
  currentPage: number;
  changePage: (page: number) => Promise<void>;
  count: number;
}

function extractTotalPages(totalRecords: number, pageSize: number) {
  return Math.ceil(Number(totalRecords) / pageSize);
}

export const Pagination = ({
  currentPage,
  totalPages,
  changePage,
  children,
  count,
}: PropsWithChildren<IPagination>) => {
  const { setIsLoading } = useLoading();
  const theme = useTheme();
  const mdUp = useMediaQuery(theme.breakpoints.up('md'));

  const handleChange = async (page: number) => {
    const [_, error] = await handleErrorAsync(async () => {
      setIsLoading(true);
      await changePage(page);
    }).finally(() => {
      setIsLoading(false);
    });

    if (error) return Toast('error', error.message);
  };

  return (
    <>
      {children}
      {totalPages !== 0 && (
        <>
          {count > 0 && (
            <Box sx={{ textAlign: 'center' }}>
              <Typography variant="subtitle1">Total: {count}</Typography>
            </Box>
          )}
          <Box
            sx={{
              width: '100%',
              display: 'flex',
              justifyContent: 'center',
              margin: '0 auto',
              py: 2,
              px: 1,
            }}
            data-testid="pagination-container"
          >
            <MuiPagination
              count={totalPages}
              page={currentPage}
              defaultPage={1}
              siblingCount={1}
              onChange={(_, page) => handleChange(page)}
              color="primary"
              size={mdUp ? 'medium' : 'small'}
            />
          </Box>
        </>
      )}
    </>
  );
};

export { extractTotalPages };
export type { IPagination };
